import {
  addDisplayName,
  addHandlers,
  addProps,
  addState,
  addStateHandlers,
  flowMax,
} from 'ad-hok'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import { isLoginError } from 'shared/src/api/types'
import { addNavigate } from 'shared/src/utils/addNavigate'
import { addAuthContext } from 'shared/src/utils/auth'
import { typedAs } from 'shared/src/utils/typedAs'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { makeRegisterUserRoute } from 'utils/routes'
import { Button } from '../Button'
import { IconButton } from '../IconButton'
import { SIDE_PADDING } from '../PageConstants'
import { TextFieldNew } from '../TextField'
import { SANS_SERIF_FONT_FAMILY, SERIF_FONT_FAMILY } from '../styles'
import { EmailVerification } from './EmailVerification'
import { RecoverPassword } from './RecoverPassword'

const useStyles = createUseStyles({
  container: {
    paddingLeft: SIDE_PADDING,
    paddingRight: SIDE_PADDING,
  },
  title: {
    fontFamily: SERIF_FONT_FAMILY,
    fontSize: 32,
    fontWeight: 600,
    lineHeight: '140%',
    letterSpacing: 0.01,
    marginTop: 16,
    marginBottom: 12,
    fontStyle: 'italic',
  },
  subtitle: {
    fontFamily: SANS_SERIF_FONT_FAMILY,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '187%',
    color: 'rgba(144, 115, 79, 0.8)',
    marginLeft: 30,
    marginBottom: 40,
  },
  passwordInput: {
    marginBottom: 8,
  },
  forgotPassword: {
    fontFamily: SANS_SERIF_FONT_FAMILY,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '160%',
    letterSpacing: 0.2,
    marginBottom: 18,
    color: 'rgba(54, 52, 50, 0.8)',
    display: 'flex',
    justifyContent: 'space-between',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  wrongCredentials: {
    color: '#F57561',
  },
  noAccountText: {
    fontFamily: SANS_SERIF_FONT_FAMILY,
    fontWeight: 700,
    fontSize: 14,
    color: 'rgba(54, 52, 50, 0.8)',
    lineHeight: '187%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 200,
    width: '100%',
    gap: 2,
  },
  linkText: {
    fontWeight: 900,
    color: '#CBA67A',
    '&:hover': {
      cursor: 'pointer',
    },
  },
})

type LoginState =
  | 'unknown'
  | 'pendingVerification'
  | 'verified'
  | 'forgotPassword'

export const Login = flowMax(
  addDisplayName('Login'),
  addNavigate,
  addState('invalidCredentials', 'setInvalidCredentials', false),
  addStateHandlers(
    {
      email: '',
      password: '',
    },
    {
      onChangeEmail: () => (newEmail: string) => ({
        email: newEmail,
      }),
      onChangePassword: () => (newPassword: string) => ({
        password: newPassword,
      }),
    }
  ),
  addState('loginState', 'setLoginState', typedAs<LoginState>('unknown')),
  addApi,
  addState('isVerifyingLogin', 'setIsVerifyingLogin', false),
  addAuthContext,
  addTranslationHelpers,
  addHandlers({
    onBack:
      ({ loginState, setLoginState, navigate }) =>
      () => {
        if (loginState === 'verified') {
          setLoginState('unknown')
        } else {
          navigate('/')
        }
      },
    onRegister:
      ({ email, navigate }) =>
      () => {
        navigate(makeRegisterUserRoute(email))
      },
    onLogin:
      ({
        api,
        email,
        password,
        setInvalidCredentials,
        setIsVerifyingLogin,
        logIn,
        navigate,
      }) =>
      () => {
        const getVerificationStatus = async () => {
          setIsVerifyingLogin(true)
          setInvalidCredentials(false)
          const response = await api.auth.logIn({
            email,
            password,
            resetPasswordToken: undefined,
          })

          if (isLoginError(response)) {
            if (response.error === 'incorrectEmailOrPassword') {
              setInvalidCredentials(true)
            }
          } else {
            logIn()
            navigate('/')
          }
          setIsVerifyingLogin(false)
        }

        getVerificationStatus()
      },
    onContinue:
      ({ api, email, setLoginState }) =>
      () => {
        const getVerificationStatus = async () => {
          const response = await api.auth.status(email, 'creatorWeb')

          if (response.status === 'pendingVerification') {
            setLoginState('pendingVerification')
          } else {
            setLoginState('verified')
          }
        }

        getVerificationStatus()
      },
    onClose:
      ({ setLoginState }) =>
      () => {
        setLoginState('unknown')
      },
    onForgotPassword:
      ({ setLoginState }) =>
      () => {
        setLoginState('forgotPassword')
      },
    resetState:
      ({ setLoginState }) =>
      () => {
        setLoginState('unknown')
      },
  }),
  addHandlers({
    onKeyPress:
      () =>
      (
        event: React.KeyboardEvent<HTMLInputElement>,
        callFunction: () => void,
        disabled: boolean
      ) => {
        if (event.key === 'Enter' && !disabled) {
          callFunction()
        }
      },
  }),
  addProps(({ email, password, isVerifyingLogin }) => ({
    onContinueDisabled: email === '',
    onLoginDisabled: isVerifyingLogin || password === '' || email === '',
  })),
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  ({
    email,
    password,
    onChangeEmail,
    onChangePassword,
    onBack,
    onRegister,
    onLogin,
    onContinue,
    onClose,
    onForgotPassword,
    loginState,
    resetState,
    onKeyPress,
    onLoginDisabled,
    invalidCredentials,
    onContinueDisabled,
    classes,
    t,
  }) => (
    <div className={classes.container}>
      {loginState === 'pendingVerification' ? (
        <EmailVerification
          email={email}
          emailType="verification"
          onClose={onClose}
        />
      ) : loginState === 'forgotPassword' ? (
        <RecoverPassword email={email} onBack={resetState} />
      ) : (
        <>
          <IconButton name="backRodeo" onClick={onBack} />
          <h1 className={classes.title}>
            {t(
              loginState === 'verified'
                ? 'auth.login.enterPasswordTitle'
                : 'auth.login.title'
            )}
          </h1>
          <p className={classes.subtitle}>
            {t(
              loginState === 'verified'
                ? 'auth.login.enterPasswordSubtitle'
                : 'auth.login.subtitle'
            )}
          </p>
          {loginState === 'unknown' && (
            <>
              <TextFieldNew
                id="email"
                label={t('auth.fields.email')}
                value={email ?? ''}
                onChange={onChangeEmail}
                focusOnMount
                showClearInput={false}
                onKeyPress={(event) =>
                  onKeyPress(event, onContinue, onContinueDisabled)
                }
              />
              <Button
                text={t('auth.login.continueButton')}
                type="fullWidth"
                onClick={onContinue}
                disabled={onContinueDisabled}
              />
            </>
          )}
          {loginState === 'verified' && email && (
            <>
              <TextFieldNew
                id="password"
                type="password"
                label={t('auth.fields.password')}
                value={password ?? ''}
                onChange={(password) => onChangePassword(password)}
                showClearInput={false}
                className={classes.passwordInput}
                autoComplete={false}
                onKeyPress={(event) =>
                  onKeyPress(event, onLogin, onLoginDisabled)
                }
              />
              <div className={classes.forgotPassword}>
                <span className={classes.wrongCredentials}>
                  {invalidCredentials && t('auth.login.wrongCredentials')}
                </span>
                <button onClick={onForgotPassword}>
                  {t('auth.forgotPassword')}
                </button>
              </div>
              <Button
                text={t('auth.login.loginButton')}
                type="fullWidth"
                onClick={onLogin}
                disabled={onLoginDisabled}
              />
            </>
          )}
          <div className={classes.noAccountText}>
            {t('auth.login.noAccount')}
            <span className={classes.linkText} onClick={onRegister}>
              {t('auth.login.register')}
            </span>
          </div>
        </>
      )}
    </div>
  )
)
